import React, { useState, useEffect } from 'react';
import './App.css';

type ViewKey = 'view1' | 'view2' | 'view3' | 'view4';

interface ViewOption {
  name: string;
  description: string;
  height: string;
  features: string[];
}

type ViewOptions = Record<ViewKey, ViewOption>;

function App() {
  const [selectedView, setSelectedView] = useState<ViewKey>('view4');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add mobile debugging
  useEffect(() => {
    console.log('App mounted');
    console.log('User Agent:', navigator.userAgent);
    console.log('Screen size:', window.screen.width, 'x', window.screen.height);
    console.log('Viewport size:', window.innerWidth, 'x', window.innerHeight);
  }, []);

  const viewOptions: ViewOptions = {
    view1: {
      name: 'Grid Layout',
      description: 'Photos, Videos, and Stories sections with comprehensive overview',
      height: '800px',
      features: ['Photo Gallery', 'Video Collection', 'Stories Archive', 'User Statistics']
    },
    view2: {
      name: 'Split Layout', 
      description: 'Interactive gallery with scrollable timeline feed',
      height: '1200px', // Increased height for mobile content
      features: ['Rotating Gallery', 'Timeline Feed', 'Split Screen', 'Interactive Controls']
    },
    view3: {
      name: 'Aspect Ratio',
      description: 'Narrow gallery with original media proportions preserved',
      height: '1200px', // Increased height for mobile content
      features: ['Original Dimensions', 'Narrow Gallery', 'Aspect Preservation', 'Responsive Design']
    },
    view4: {
      name: 'Gallery Only',
      description: 'Centered iPhone gallery showcase for focused presentation',
      height: '600px',
      features: ['Minimal Design', 'Centered Layout', 'Gallery Focus', 'Clean Interface']
    }
  };

  const handleViewChange = (viewKey: ViewKey): void => {
    setIsLoading(true);
    setSelectedView(viewKey);
    // Simulate loading time for better UX
    setTimeout(() => setIsLoading(false), 500);
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <img src="/GreenPackagingPro-logo.png" alt="GreenPackaging Pro" className="logo-img" />
            <h1>GreenPackaging Pro</h1>
          </div>
          <nav className="nav">
            <a href="#home">Home</a>
            <a href="#products">Products</a>
            <a href="#about">About</a>
            <a href="#portfolio">Portfolio</a>
            <a href="#contact">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container">
          <div className="hero-content">
            <h2>Premium Mylar Bags & Custom Box Packaging Solutions</h2>
            <p>Protect your products with our high-quality, sustainable packaging solutions. From food-grade mylar bags to custom printed boxes, we've got your packaging needs covered.</p>
            <div className="hero-buttons">
              <button className="btn-primary">Get Quote</button>
              <button className="btn-secondary">View Products</button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="products">
        <div className="container">
          <h2>Our Products</h2>
          <div className="product-grid">
            <div className="product-card">
              <div className="product-icon">📦</div>
              <h3>Mylar Bags</h3>
              <p>Food-grade, moisture-resistant mylar bags in various sizes. Perfect for long-term storage and product freshness.</p>
              <ul>
                <li>Multiple sizes available</li>
                <li>Heat sealable</li>
                <li>FDA approved materials</li>
                <li>Custom printing options</li>
              </ul>
            </div>
            <div className="product-card">
              <div className="product-icon">📋</div>
              <h3>Custom Boxes</h3>
              <p>Durable corrugated boxes with custom printing and branding. Ideal for shipping and retail packaging.</p>
              <ul>
                <li>Custom sizes & designs</li>
                <li>High-quality printing</li>
                <li>Eco-friendly materials</li>
                <li>Fast turnaround</li>
              </ul>
            </div>
            <div className="product-card">
              <div className="product-icon">🌱</div>
              <h3>Sustainable Options</h3>
              <p>Environmentally conscious packaging solutions that don't compromise on quality or protection.</p>
              <ul>
                <li>Recyclable materials</li>
                <li>Biodegradable options</li>
                <li>Reduced carbon footprint</li>
                <li>Sustainable sourcing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About GreenPackaging Pro</h2>
              <p>With over a decade of experience in the packaging industry, GreenPackaging Pro has been the trusted partner for businesses seeking high-quality, reliable packaging solutions.</p>
              <p>We specialize in mylar bags and custom box packaging, serving industries from food and beverage to electronics and pharmaceuticals. Our commitment to quality, sustainability, and customer satisfaction sets us apart.</p>
              <div className="stats">
                <div className="stat">
                  <h3>10,000+</h3>
                  <p>Happy Customers</p>
                </div>
                <div className="stat">
                  <h3>50M+</h3>
                  <p>Packages Delivered</p>
                </div>
                <div className="stat">
                  <h3>99.9%</h3>
                  <p>Quality Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Gallery Section */}
      <section id="portfolio" className="portfolio">
        <div className="container">
          <h2>Our Work in Action</h2>
          <p>See our packaging solutions in different presentation styles</p>
          
          {/* View Selector Tabs */}
          <div className="view-selector">
            <div className="view-tabs">
              {(Object.entries(viewOptions) as [ViewKey, ViewOption][]).map(([viewKey, viewInfo]) => (
                <button
                  key={viewKey}
                  className={`view-tab ${selectedView === viewKey ? 'active' : ''}`}
                  onClick={() => handleViewChange(viewKey)}
                >
                  <div className="tab-title">{viewInfo.name}</div>
                  <div className="tab-description">{viewInfo.description}</div>
                  <div className="tab-features">
                    {viewInfo.features.map((feature: string, index: number) => (
                      <span key={index} className="feature-tag">{feature}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Dynamic Embed Container */}
          <div id="social-upload-embed" style={{width: '100%', minHeight: '500px', margin: '2rem 0'}}>
            <div className="embed-header">
              <h3>Current View: {viewOptions[selectedView].name}</h3>
              <p>{viewOptions[selectedView].description}</p>
            </div>
            
            {isLoading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Loading {viewOptions[selectedView].name}...</p>
              </div>
            )}
            
            <iframe 
              src={`https://d10o1bqsal7ksk.cloudfront.net/embed/${selectedView}`}
              width="100%" 
              height={viewOptions[selectedView].height}
              frameBorder="0" 
              scrolling="yes"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{
                border: 'none', 
                borderRadius: '8px', 
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                opacity: isLoading ? 0.3 : 1,
                transition: 'opacity 0.3s ease',
                minHeight: viewOptions[selectedView].height
              }}
              title={`Portfolio Gallery - ${viewOptions[selectedView].name}`}
              key={selectedView} // Force iframe reload when view changes
              onLoad={() => setIsLoading(false)}
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2>Get In Touch</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Contact Information</h3>
              <div className="contact-item">
                <strong>Phone:</strong> <span>(555) 123-4567</span>
              </div>
              <div className="contact-item">
                <strong>Email:</strong> <span>info@greenpackagingpro.com</span>
              </div>
              <div className="contact-item">
                <strong>Address:</strong> <span>123 Packaging Way, Industrial District, City, State 12345</span>
              </div>
              <div className="contact-item">
                <strong>Hours:</strong> <span>Monday - Friday: 8:00 AM - 6:00 PM</span>
              </div>
            </div>
            <div className="contact-form">
              <h3>Request a Quote</h3>
              <form>
                <input type="text" placeholder="Your Name" required />
                <input type="email" placeholder="Your Email" required />
                <input type="tel" placeholder="Phone Number" />
                <select required>
                  <option value="">Select Product Type</option>
                  <option value="mylar-bags">Mylar Bags</option>
                  <option value="custom-boxes">Custom Boxes</option>
                  <option value="both">Both</option>
                  <option value="other">Other</option>
                </select>
                <textarea placeholder="Tell us about your packaging needs..." rows={4} required></textarea>
                <button type="submit" className="btn-primary">Send Request</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>GreenPackaging Pro</h3>
              <p>Your trusted partner for premium packaging solutions.</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <a href="#home">Home</a>
              <a href="#products">Products</a>
              <a href="#about">About</a>
              <a href="#portfolio">Portfolio</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="footer-section">
              <h4>Services</h4>
              <a href="#products">Mylar Bags</a>
              <a href="#products">Custom Boxes</a>
              <a href="#products">Sustainable Packaging</a>
              <a href="#contact">Custom Solutions</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 GreenPackaging Pro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
